import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { 
  BarChart3, 
  TrendingUp, 
  Target, 
  Clock, 
  CheckCircle, 
  Users, 
  Star,
  Award,
  Calendar,
  Activity
} from 'lucide-react';

interface EmployeePerformanceAnalysisProps {
  employeeId: string;
}

interface PerformanceMetrics {
  totalTasks: number;
  completedTasks: number;
  onTimeTasks: number;
  overdueTasks: number;
  totalClients: number;
  activeClients: number;
  totalPackages: number;
  completedPackages: number;
  averageTaskDuration: number;
  monthlyPerformance: {
    month: string;
    tasksCompleted: number;
    clientsServed: number;
  }[];
}

export function EmployeePerformanceAnalysis({ employeeId }: EmployeePerformanceAnalysisProps) {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformanceMetrics();
  }, [employeeId]);

  const fetchPerformanceMetrics = async () => {
    try {
      setLoading(true);

      // جلب المهام
      const { data: tasks } = await supabase
        .from('tasks')
        .select('id, status, completed_at, due_date, start_date, assigned_to')
        .eq('assigned_to', employeeId);

      // جلب العملاء المرتبطين بالموظف
      const { data: clientAssignments } = await supabase
        .from('client_assignments')
        .select('client_id, created_at')
        .eq('employee_id', employeeId);

      // جلب الباقات المرتبطة بالموظف
      const { data: packages } = await supabase
        .from('package_bookings')
        .select('id, status, created_at, employee_id')
        .eq('employee_id', employeeId);

      // حساب المقاييس
      const totalTasks = tasks?.length || 0;
      const completedTasks = tasks?.filter(t => t.status === 'completed').length || 0;
      const onTimeTasks = tasks?.filter(t => 
        t.status === 'completed' && 
        t.completed_at && 
        t.due_date && 
        new Date(t.completed_at) <= new Date(t.due_date)
      ).length || 0;
      const overdueTasks = tasks?.filter(t => 
        t.due_date && 
        new Date(t.due_date) < new Date() && 
        t.status !== 'completed'
      ).length || 0;

      const totalClients = clientAssignments?.length || 0;
      // تقدير العملاء النشطين بناءً على التعيينات الحديثة
      const recentThreshold = new Date();
      recentThreshold.setMonth(recentThreshold.getMonth() - 3);
      const activeClients = clientAssignments?.filter(ca => 
        ca.created_at && new Date(ca.created_at) >= recentThreshold
      ).length || 0;

      const totalPackages = packages?.length || 0;
      const completedPackages = packages?.filter(p => p.status === 'completed' || p.status === 'confirmed').length || 0;

      // حساب متوسط مدة المهام
      const completedTasksWithDuration = tasks?.filter(t => 
        t.status === 'completed' && t.start_date && t.completed_at
      ) || [];
      
      const averageTaskDuration = completedTasksWithDuration.length > 0 
        ? completedTasksWithDuration.reduce((acc, task) => {
            const start = new Date(task.start_date);
            const end = new Date(task.completed_at);
            return acc + (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
          }, 0) / completedTasksWithDuration.length
        : 0;

      // حساب الأداء الشهري
      const monthlyPerformance = generateMonthlyPerformance(tasks || [], clientAssignments || []);

      const performanceMetrics: PerformanceMetrics = {
        totalTasks,
        completedTasks,
        onTimeTasks,
        overdueTasks,
        totalClients,
        activeClients,
        totalPackages,
        completedPackages,
        averageTaskDuration,
        monthlyPerformance
      };

      setMetrics(performanceMetrics);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlyPerformance = (tasks: any[], clientAssignments: { client_id: string; created_at: string }[]) => {
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const tasksCompleted = tasks.filter(t => 
        t.status === 'completed' && 
        t.completed_at &&
        new Date(t.completed_at) >= monthStart &&
        new Date(t.completed_at) <= monthEnd
      ).length;

      const clientsServed = clientAssignments.filter(ca =>
        ca.created_at &&
        new Date(ca.created_at) >= monthStart &&
        new Date(ca.created_at) <= monthEnd
      ).length;

      months.push({
        month: date.toLocaleDateString('ar-SA', { month: 'long', year: 'numeric' }),
        tasksCompleted,
        clientsServed
      });
    }
    
    return months;
  };

  const calculateCompletionRate = () => {
    if (!metrics || metrics.totalTasks === 0) return 0;
    return Math.round((metrics.completedTasks / metrics.totalTasks) * 100);
  };

  const calculateOnTimeRate = () => {
    if (!metrics || metrics.completedTasks === 0) return 0;
    return Math.round((metrics.onTimeTasks / metrics.completedTasks) * 100);
  };

  const calculateClientSatisfactionRate = () => {
    if (!metrics || metrics.totalClients === 0) return 0;
    return Math.round((metrics.activeClients / metrics.totalClients) * 100);
  };

  const getPerformanceLevel = (rate: number) => {
    if (rate >= 90) return { label: 'ممتاز', color: 'bg-green-500', variant: 'default' as const };
    if (rate >= 80) return { label: 'جيد جداً', color: 'bg-blue-500', variant: 'default' as const };
    if (rate >= 70) return { label: 'جيد', color: 'bg-yellow-500', variant: 'secondary' as const };
    if (rate >= 60) return { label: 'مقبول', color: 'bg-orange-500', variant: 'secondary' as const };
    return { label: 'يحتاج تحسين', color: 'bg-red-500', variant: 'destructive' as const };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-8">
        <Activity className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">لا توجد بيانات أداء</h3>
        <p className="text-muted-foreground">لم يتم العثور على بيانات لتحليل الأداء</p>
      </div>
    );
  }

  const completionRate = calculateCompletionRate();
  const onTimeRate = calculateOnTimeRate();
  const clientSatisfactionRate = calculateClientSatisfactionRate();

  return (
    <div className="space-y-6">
      {/* مؤشرات الأداء الرئيسية */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">معدل إنجاز المهام</p>
                <p className="text-2xl font-bold">{completionRate}%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div className="mt-2">
              <Progress value={completionRate} className="h-2" />
            </div>
            <div className="mt-2">
              <Badge variant={getPerformanceLevel(completionRate).variant}>
                {getPerformanceLevel(completionRate).label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">الالتزام بالمواعيد</p>
                <p className="text-2xl font-bold">{onTimeRate}%</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
            <div className="mt-2">
              <Progress value={onTimeRate} className="h-2" />
            </div>
            <div className="mt-2">
              <Badge variant={getPerformanceLevel(onTimeRate).variant}>
                {getPerformanceLevel(onTimeRate).label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">رضا العملاء</p>
                <p className="text-2xl font-bold">{clientSatisfactionRate}%</p>
              </div>
              <Star className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="mt-2">
              <Progress value={clientSatisfactionRate} className="h-2" />
            </div>
            <div className="mt-2">
              <Badge variant={getPerformanceLevel(clientSatisfactionRate).variant}>
                {getPerformanceLevel(clientSatisfactionRate).label}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">نظرة عامة</TabsTrigger>
          <TabsTrigger value="tasks">إدارة المهام</TabsTrigger>
          <TabsTrigger value="clients">خدمة العملاء</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">إجمالي المهام</p>
                    <p className="text-3xl font-bold">{metrics.totalTasks}</p>
                  </div>
                  <Target className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">المهام المكتملة</p>
                    <p className="text-3xl font-bold">{metrics.completedTasks}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">إجمالي العملاء</p>
                    <p className="text-3xl font-bold">{metrics.totalClients}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">الباقات المكتملة</p>
                    <p className="text-3xl font-bold">{metrics.completedPackages}</p>
                  </div>
                  <Award className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* الأداء الشهري */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                الأداء خلال الأشهر الستة الماضية
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {metrics.monthlyPerformance.map((month, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{month.month}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">المهام</p>
                        <p className="font-bold">{month.tasksCompleted}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">العملاء</p>
                        <p className="font-bold">{month.clientsServed}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>إحصائيات المهام</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>إجمالي المهام</span>
                  <Badge variant="outline">{metrics.totalTasks}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>المهام المكتملة</span>
                  <Badge variant="default">{metrics.completedTasks}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>المهام في الوقت المحدد</span>
                  <Badge variant="default">{metrics.onTimeTasks}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>المهام المتأخرة</span>
                  <Badge variant="destructive">{metrics.overdueTasks}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>متوسط الأداء</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>متوسط مدة المهمة</span>
                  <Badge variant="outline">
                    {Math.round(metrics.averageTaskDuration)} يوم
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>معدل الإنجاز</span>
                  <Badge variant="default">{completionRate}%</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>معدل الالتزام بالمواعيد</span>
                  <Badge variant="default">{onTimeRate}%</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="clients" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>إحصائيات العملاء</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>إجمالي العملاء المعينين</span>
                  <Badge variant="outline">{metrics.totalClients}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>العملاء النشطون</span>
                  <Badge variant="default">{metrics.activeClients}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>معدل رضا العملاء</span>
                  <Badge variant="default">{clientSatisfactionRate}%</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>الباقات السياحية</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>إجمالي الباقات</span>
                  <Badge variant="outline">{metrics.totalPackages}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>الباقات المكتملة</span>
                  <Badge variant="default">{metrics.completedPackages}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>معدل إكمال الباقات</span>
                  <Badge variant="default">
                    {metrics.totalPackages > 0 
                      ? Math.round((metrics.completedPackages / metrics.totalPackages) * 100)
                      : 0
                    }%
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}